
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Modal,
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Paper,
  IconButton,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import StarIcon from '@mui/icons-material/Star';
import SimpleCustomTable from '../../../../components/table/SimpleCustomTable';
import { SimpleColumn } from '../../../../components/table/interface';
import moment from 'moment-timezone';

interface HistoricoModalProps {
  open: boolean;
  onClose: () => void;
  clientId: string | null; // Permitir null
  collectId: string | null; // Permitir null
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface ClientInfo {
  id: number;
  idnumber: string;
  fullname: string;
  phone: string;
  cellphone: string;
  mainaddress: string;
  secondaddress: string;
  job: string;
  coclient_fullname: string;
  coclient_idnumber: string;
  coclient_address: string;
  coclient_phone: string;
  collect: {
    id: number;
    name: string;
    ingress_date: string;
    time_open: string;
    time_close: string;
    quote: number;
    detail_machine: boolean;
    valid_time: boolean;
    enable_repay: boolean;
    enable_more_lend: boolean;
    enable_refinance: boolean;
  };
  lend: {
    id: number;
    date: string;
    date_state: string;
    value: number;
    total_value: number;
    balance: number;
    utility: number;
    daily_pay_value: number;
    days_term: number;
    days_late: number;
    state: number;
    will_pay: number;
    current_pay: number;
    current_day: number;
    route_id: number;
    route_day: string | null;
    position: number;
    contract: string;
    count_pay: number;
    location: string;
  }[];
}
const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 800,
  height: 600,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const HistoricoModal: React.FC<HistoricoModalProps> = ({
  open,
  onClose,
  collectId,
  clientId,
  showAlert,
  showLoading,
  hideLoading,
}) => {
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [inputs, setInputs] = useState({
    cedula: '',
    nombre: '',
    direccion: '',
    telefono: '',
    oficio: '',
    calificacion: '',
  });

  useEffect(() => {
    const fetchClientInfo = async () => {
      try {
        showLoading();
        if (!clientId) {
          return;
        }
        const url = "/collectclient";
        const formData: any = {
          idnumber: clientId,
        };
        if (collectId) {
          formData.collect_id = collectId;
        }
        const response = await axios.post(url, formData);
        if (response.data.data.length > 0) {
          const info = response.data.data[0] as ClientInfo;
          setClientInfo(info);
          setInputs({
            cedula: info.idnumber?.toString() || '',
            nombre: info.fullname || '',
            direccion: info.mainaddress || '',
            telefono: info.phone || '',
            oficio: info.job || '',
            calificacion: '',
          });
          const mappedRows = (info.lend || []).map((lend) => ({
            id: lend.id,
            state: lend.state === 0 ? 'Vigente' : 'Cancelado',
            date: lend.date ? moment(lend.date).format('YYYY/MM/DD') : '',
            date_state: lend.date_state ? moment(lend.date_state).format('YYYY/MM/DD') : '',
            value: lend.value ?? 0,
            collect: info.collect?.name || '',
          }));
          setRows(mappedRows);
        } else {
          setClientInfo(null);
          setRows([]);
          showAlert('No se encontraron datos para el cliente.', 'info');
        }
      } catch (error) {
        showAlert('Error al obtener la información del cliente.', 'error');
      } finally {
        hideLoading();
      }
    };

    if (open) {
      fetchClientInfo();
    }
  }, [open, collectId, clientId, showAlert, showLoading, hideLoading]);

  const handleClose = () => {
    setClientInfo(null);
    setRows([]);
    onClose();
  };

  const historialColumns: SimpleColumn[] = [
    { field: 'id', headerName: 'Número', width: '80px' },
    { field: 'state', headerName: 'Estado', width: '110px' },
    { field: 'date', headerName: 'Fecha', width: '110px' },
    { field: 'date_state', headerName: 'Fecha Cancela', width: '120px' },
    { field: 'value', headerName: 'Valor', width: '110px' },
    { field: 'collect', headerName: 'Cobro', width: '140px' },
  ];

  return (
    <Modal open={open} onClose={handleClose}>
      <Paper sx={modalStyle}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            bgcolor: 'primary.main',
            color: 'white',
          }}
        >
          <Typography variant="h6">Información Cliente</Typography>
          <IconButton onClick={handleClose} sx={{ color: 'white' }} aria-label="cerrar">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, overflowY: 'auto', flexGrow: 1 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={7} md={6} lg={6}>
              <TextField
                label="Cedula"
                name="cedula"
                value={inputs.cedula}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                label="Nombre"
                name="nombre"
                value={inputs.nombre}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                label="Dirección"
                name="direccion"
                value={inputs.direccion}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <HomeIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                label="Teléfono"
                name="telefono"
                value={inputs.telefono}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                label="Oficio"
                name="oficio"
                value={inputs.oficio}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <WorkIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <TextField
                label="Calificación"
                name="calificacion"
                value={inputs.calificacion}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <StarIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Historial de préstamos
          </Typography>
          <Box
            sx={{
              width: '100%',
              '& .MuiTableCell-body': { padding: '4px 8px' },
              '& .MuiTableCell-head': { padding: '6px 8px' },
            }}
          >
            <SimpleCustomTable
              rows={rows}
              columns={historialColumns}
              maxHeight={300}
              showBorders
              wrapCellContent
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Button variant="outlined" onClick={handleClose}>
            Cerrar
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default HistoricoModal;
